import React, { useCallback, useContext, useEffect, useRef } from 'react';

import { useLocation, useNavigate, Outlet } from 'react-router-dom';

import { ApiContext } from '_lib/api/apiContext';
import {
  APP_VERSION_NOT_SUPPORTED,
  FORCE_APP_UPDATE_EVENT,
  INVALID_AUTH,
  INVALID_PROVIDER_AUTH_EVENT,
  isProviderAuthErrorEvent,
  isUnsupportedAppVersionErrorEvent
} from '_lib/api/apiService';
import { RoleEnum } from '_lib/api/apiTypes.shim';
import { ServiceWorkerContextProvider } from '_lib/api/serviceWorkerContextProvider';
import { AuthContext } from '_lib/featureComponents/auth/authContext';
import { appRoutePaths, getFullAppRoutePath } from '_lib/featureComponents/Nav/nav.types';
import { useAlertDialog } from '_lib/hooks/AlertDialogHook';
import { useSelectCallCenterDialog } from '_lib/hooks/SelectCallCenterDialogHook';
import { logDebug } from '_lib/utils/logging';
import { setReportingUser } from '_lib/utils/sentryService';

export const RouterRoot = (): React.ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();

  const alertDialog = useAlertDialog();
  const callCenterDialog = useSelectCallCenterDialog();

  const api = useContext(ApiContext);

  const { authUserCacheResource, callCenterCacheResource, logout } = useContext(AuthContext);

  const cachedAuthUserQuery = authUserCacheResource.useCacheQuery();
  const cachedCallCenterQuery = callCenterCacheResource.useCacheQuery();

  useEffect(() => {
    logDebug('Location Changed. New Location: ', location);
  }, [location]);

  const handleInvalidAuth = useCallback(() => {
    logout();
    navigate('auth/login');
  }, [logout, navigate]);

  useEffect(() => {
    window.addEventListener(INVALID_AUTH, handleInvalidAuth);
    return () => {
      window.removeEventListener(INVALID_AUTH, handleInvalidAuth);
    };
  }, [navigate, handleInvalidAuth]);

  const dialogIsOpen = useRef(false);
  useEffect(() => {
    const dialogFunction = async () => {
      if (
        cachedAuthUserQuery?.data?.role === RoleEnum.SYSTEM &&
        !cachedCallCenterQuery.data &&
        !cachedCallCenterQuery.isFetching &&
        dialogIsOpen.current === false
      ) {
        dialogIsOpen.current = true;

        const callCenters = await api.callCenters.unpagedList({});
        if (callCenters.results?.length === 1) {
          const callCenter = await api.callCenters.getById({ id: callCenters.results[0]!.id });
          await callCenterCacheResource.set(callCenter).catch(() => {});
          navigate(getFullAppRoutePath(appRoutePaths.CallLog));
        } else if (callCenters.results.length) {
          await callCenterDialog.show({ titleText: 'Select a CallCenter', callCenters: callCenters.results });
        } else {
          handleInvalidAuth();
        }

        dialogIsOpen.current = false;
      }
    };

    dialogFunction();
  }, [
    cachedAuthUserQuery?.data?.role,
    cachedCallCenterQuery.isFetching,
    cachedCallCenterQuery.data,
    callCenterDialog,
    location,
    api.callCenters,
    callCenterCacheResource,
    handleInvalidAuth,
    navigate
  ]);

  useEffect(() => {
    const handleInvalidProviderAuth = async (e: Event) => {
      if (isProviderAuthErrorEvent(e)) {
        await alertDialog.show({
          variant: 'error',
          titleText: 'UNEXPECTED INTEGRATION ERROR',
          content: e.detail.message
        });
      }
    };

    window.addEventListener(INVALID_PROVIDER_AUTH_EVENT, handleInvalidProviderAuth);
    return () => {
      window.removeEventListener(INVALID_PROVIDER_AUTH_EVENT, handleInvalidProviderAuth);
    };
  }, [alertDialog]);

  /**
   * Setup event handler to capture any app update events felt by the API client. Handle by showing a
   * dialog that triggers an update after it is acknowledged.
   */
  useEffect(() => {
    const handleUnsupportedAppVersion = async (e: Event) => {
      if (isUnsupportedAppVersionErrorEvent(e)) {
        await alertDialog.show({
          variant: 'error',
          titleText: 'UPDATE APP',
          content: e.detail.message,
          renderMarkup: true
        });
        window.dispatchEvent(new Event(FORCE_APP_UPDATE_EVENT));
      }
    };
    window.addEventListener(APP_VERSION_NOT_SUPPORTED, handleUnsupportedAppVersion);
    return () => {
      window.removeEventListener(APP_VERSION_NOT_SUPPORTED, handleUnsupportedAppVersion);
    };
  }, [alertDialog]);

  useEffect(() => {
    if (cachedAuthUserQuery.data) {
      setReportingUser(cachedAuthUserQuery.data);
    }
  }, [cachedAuthUserQuery.data]);

  return (
    // react-router dependent providers
    <ServiceWorkerContextProvider>
      <Outlet />
      {alertDialog.element}
      {callCenterDialog.element}
    </ServiceWorkerContextProvider>
  );
};
